import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Paper, Button, Grid, Chip, Divider, Menu, MenuItem, Avatar, Container, Stack, Grow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SubjectIcon from '@mui/icons-material/Subject';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function ReferenceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${API_URL}/references/${id}`, { headers });
      setReference(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const handleStatusClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    handleStatusClose();
    try {
      await axios.put(`${API_URL}/references/${id}`, { status: newStatus }, { headers });
      fetchDetails(); // Refresh
    } catch (err) {
      alert("Error updating status");
    }
  };

  if (loading) return <Loading message="Loading Reference Details..." />;
  if (!reference) return <Typography>Reference not found</Typography>;

  const getStatusColor = (status) => {
    switch(status) {
        case 'Approved': return 'success';
        case 'Pending': return 'warning';
        case 'Rejected': return 'error';
        default: return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Header Section */}
      <Grow in={true} timeout={500}>
      <Box sx={{ 
        p: 4, 
        mt: 2,
        mb: 4, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
        color: 'white',
        boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', bottom: -20, right: 100, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 1 }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, justifyContent: 'space-between', gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/references')} 
                        sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                    >
                        Back to References
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <Typography variant="h5" fontWeight="900" sx={{ letterSpacing: 0.3, wordBreak: 'break-word', color: 'white', lineHeight: 1.3 }}>
                            {reference.ref_number}
                        </Typography>
                        <Chip 
                            label={reference.status} 
                            onClick={handleStatusClick}
                            onDelete={handleStatusClick}
                            deleteIcon={<EditIcon sx={{ color: `${getStatusColor(reference.status)}.main` }} />}
                            sx={{ 
                                bgcolor: 'white', 
                                color: 'primary.main', 
                                fontWeight: 'bold',
                                height: 26,
                                cursor: 'pointer',
                                flexShrink: 0
                            }} 
                        />
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleStatusClose}
                    >
                        <MenuItem onClick={() => handleStatusChange('Pending')}>Pending</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('Approved')}>Approved</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('Rejected')}>Rejected</MenuItem>
                    </Menu>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 0.5 }}>
                         <Chip label={reference.type} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                         
                         {reference.project_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'white' }}>
                                <DescriptionIcon fontSize="small" sx={{ color: 'white' }} />
                                <Typography variant="body2" sx={{ color: 'white' }}>{reference.project_name}</Typography>
                            </Box>
                         )}
                    </Box>
                </Box>

                {/* QR Code Box */}
                <Box sx={{ 
                    bgcolor: 'white', 
                    p: 1.5, 
                    borderRadius: 2, 
                    boxShadow: 3,
                    textAlign: 'center',
                    flexShrink: 0
                }}>
                    <img 
                        src={`${API_URL}/references/${id}/qr`} 
                        alt="QR Code" 
                        style={{ width: 100, height: 100, display: 'block' }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                        SCAN ME
                    </Typography>
                </Box>
            </Box>
        </Box>
      </Box>
      </Grow>

      {/* Main Content Paper */}
      <Grow in={true} timeout={800}>
      <Paper sx={{ mb: 3, p: 4, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <Grid container spacing={5}>
                {/* Main Info */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>SUBJECT</Typography>
                        <Typography variant="h5" fontWeight="bold">
                            {reference.subject}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 5, opacity: 0.6 }} />

                    <Grid container spacing={4} sx={{ mb: 5 }}>
                        <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <Typography variant="overline" color="primary.main" fontWeight="800" sx={{ letterSpacing: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon fontSize="small" /> RECIPIENT
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'primary.100', color: 'primary.700', width: 40, height: 40 }}>
                                        <PersonIcon />
                                    </Avatar>
                                    <Typography variant="body1" fontWeight="600" color="text.primary">
                                        {reference.recipient_to || '—'}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <Typography variant="overline" color="secondary.main" fontWeight="800" sx={{ letterSpacing: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOnIcon fontSize="small" /> ADDRESS
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'secondary.100', color: 'secondary.700', width: 40, height: 40 }}>
                                        <LocationOnIcon />
                                    </Avatar>
                                    <Typography variant="body1" fontWeight="600" color="text.primary">
                                        {reference.address || '—'}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box>
                        <Typography variant="overline" color="success.main" fontWeight="800" sx={{ letterSpacing: 1.5, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <DescriptionIcon fontSize="small" /> DESCRIPTION
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 4, bgcolor: '#ffffff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'text.secondary' }}>
                                {reference.description || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No description provided.</span>}
                            </Typography>
                        </Paper>
                    </Box>
                </Grid>

                {/* Sidebar Info */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ 
                        bgcolor: '#f8fafc', 
                        p: 4, 
                        borderRadius: 4,
                        border: '1px solid #e2e8f0',
                        height: '100%'
                    }}>
                        <Typography variant="h6" fontWeight="800" sx={{ mb: 3, pb: 2, borderBottom: '2px solid #e2e8f0', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SubjectIcon /> Metadata
                        </Typography>
                        
                        <Stack spacing={3}>
                            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Typography variant="caption" fontWeight="800" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                    <CalendarTodayIcon fontSize="inherit" /> DATE CREATED
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: '#f1f5f9', width: 36, height: 36 }}>
                                        <CalendarTodayIcon fontSize="small" sx={{ color: '#64748b' }} />
                                    </Avatar>
                                    <Typography variant="body1" fontWeight="600">
                                        {reference.created_at}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Typography variant="caption" fontWeight="800" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                    <AccountCircleIcon fontSize="inherit" /> CREATED BY
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ 
                                        width: 42, 
                                        height: 42, 
                                        bgcolor: 'primary.main', 
                                        fontWeight: '800',
                                        boxShadow: '0 4px 10px rgba(25,118,210,0.2)' 
                                    }}>
                                        {reference.user_name ? reference.user_name.charAt(0).toUpperCase() : 'U'}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body1" fontWeight="800" sx={{ color: 'text.primary' }}>
                                            {reference.user_name || 'Unknown User'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            Creator
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
      </Paper>
      </Grow>
    </Box>
  );
}

export default ReferenceDetails;
